import { createColumnHelper } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { defaultColumnHelperAccessor } from 'src/tables/ReportTableCommon/DefaultColumnHelperAccessor';
import { LinkCell } from 'src/tables/ReportTableCommon/LinkCell';
import { SHIMMERING_DIV_HEIGHT_REPORT } from 'src/tables/ReportTableCommon/ReportTableCommon.constants';
import { reportV2AccessorFn } from 'src/tables/ReportTableCommon/ReportTableCommon.utils';
import { TableShimmeringDiv } from 'src/tables/Table';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  SaleCostAdjustmentsReportTableGroupingColumnId,
  SaleCostAdjustmentsReportTableValueColumnId,
} from 'src/utils/columns/saleCostAdjustments/saleCostAdjustmentsColumnUtils.types';
import {
  SALE_COST_ADJUSTMENTS_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID,
  SALE_COST_ADJUSTMENTS_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { ReportValueType } from 'src/WebApiController';

const columnHelper = createColumnHelper<ReportMetricsResponse | null>();

export const SALE_COST_ADJUSTMENTS_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG = {
  [SaleCostAdjustmentsReportTableGroupingColumnId.AuditMonth]:
    columnHelper.accessor(
      reportV2AccessorFn(
        SaleCostAdjustmentsReportTableGroupingColumnId.AuditMonth
      ),
      {
        id: SaleCostAdjustmentsReportTableGroupingColumnId.AuditMonth,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                SALE_COST_ADJUSTMENTS_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  SaleCostAdjustmentsReportTableGroupingColumnId.AuditMonth
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          const { timeZone } = useSiteTimezoneContext();
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue
                  ? formatInTimeZone(
                      new Date(dateValue),
                      timeZone ?? 'UTC',
                      'MMMM yyyy'
                    )
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
  [SaleCostAdjustmentsReportTableGroupingColumnId.AuditWeek]:
    columnHelper.accessor(
      reportV2AccessorFn(
        SaleCostAdjustmentsReportTableGroupingColumnId.AuditWeek
      ),
      {
        id: SaleCostAdjustmentsReportTableGroupingColumnId.AuditWeek,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                SALE_COST_ADJUSTMENTS_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  SaleCostAdjustmentsReportTableGroupingColumnId.AuditWeek
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          const { timeZone } = useSiteTimezoneContext();
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          const dateEndOfWeek = dateValue ? new Date(dateValue) : null;
          if (dateEndOfWeek) {
            dateEndOfWeek.setDate(dateEndOfWeek.getDate() + 6);
          }

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue && dateEndOfWeek
                  ? formatInTimeZone(
                      new Date(dateValue),
                      timeZone ?? 'UTC',
                      'yyyy/MM/dd'
                    ) +
                    ' - ' +
                    formatInTimeZone(
                      dateEndOfWeek,
                      timeZone ?? 'UTC',
                      'yyyy/MM/dd'
                    )
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
  [SaleCostAdjustmentsReportTableGroupingColumnId.AuditDay]:
    columnHelper.accessor(
      reportV2AccessorFn(
        SaleCostAdjustmentsReportTableGroupingColumnId.AuditDay
      ),
      {
        id: SaleCostAdjustmentsReportTableGroupingColumnId.AuditDay,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                SALE_COST_ADJUSTMENTS_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  SaleCostAdjustmentsReportTableGroupingColumnId.AuditDay
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          const { timeZone } = useSiteTimezoneContext();
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue
                  ? formatInTimeZone(
                      new Date(dateValue),
                      timeZone ?? 'UTC',
                      'yyyy/MM/dd'
                    )
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
  [SaleCostAdjustmentsReportTableGroupingColumnId.SaleId]:
    columnHelper.accessor(
      reportV2AccessorFn(SaleCostAdjustmentsReportTableGroupingColumnId.SaleId),
      {
        id: SaleCostAdjustmentsReportTableGroupingColumnId.SaleId,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                SALE_COST_ADJUSTMENTS_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  SaleCostAdjustmentsReportTableGroupingColumnId.SaleId
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue }) => {
          const saleId = getValue<string | null>();

          if (!saleId) {
            return <></>;
          }

          return (
            <LinkCell
              text={`#${saleId}`}
              url={getSaleDetailsRelativeUrl(saleId)}
              separator={<span>{', '}</span>}
              openInNewTab
              align="left"
              style={{ fontWeight: 600 }}
            />
          );
        },
      }
    ),
} as const;

export const saleCostAdjustmentsReportTableColumnDef = (
  columnId: string,
  columnValue: string,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined
) => {
  const specialConfig = Object.entries(
    SALE_COST_ADJUSTMENTS_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG
  ).find(([key]) => key === columnId)?.[1];

  if (specialConfig) {
    return specialConfig;
  }

  const headerContentId =
    SALE_COST_ADJUSTMENTS_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
      columnId as SaleCostAdjustmentsReportTableGroupingColumnId
    ] ||
    SALE_COST_ADJUSTMENTS_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID[
      columnId as SaleCostAdjustmentsReportTableValueColumnId
    ];

  return defaultColumnHelperAccessor(
    columnHelper,
    columnId,
    columnValue,
    headerContentId,
    ReportTypesV2.SaleCostAdjustments,
    valueType,
    personalizationFlags
  );
};
